import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { ConversationSidePanelHeader } from "@app/components/assistant/conversation/ConversationSidePanelHeader";
import { DEFAULT_RIGHT_PANEL_SIZE } from "@app/components/assistant/conversation/constant";
import { usePanelFullScreen } from "@app/components/assistant/conversation/usePanelFullScreen";
import { callMembersOf } from "@app/components/live/liveHuddleContext";
import { markLivePanelClosed } from "@app/components/live/livePanelPreference";
import { MemoairLiveHostProvider } from "@app/components/live/MemoairLiveHost";
import { useConversationParticipants } from "@app/hooks/conversations/useConversationParticipants";
import { useAppRouter } from "@app/lib/platform";
import { useLiveDocConnection } from "@app/lib/swr/live_docs";
import { getLiveDocPageRoute } from "@app/lib/utils/router";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  cn,
  LinkExternal01,
  Maximize01,
  Minimize01,
} from "@dust-tt/sparkle";
import { LiveObjectView } from "@memo-air/react/all";
import { useEffect, useRef, useState } from "react";

const desktopPanelSize = 60;
const panelExitMs = 180;

interface LiveDocPanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

export function LiveDocPanel({ conversation, owner }: LiveDocPanelProps) {
  const {
    data: docId = "",
    closePanel,
    backToDetails,
    panelRef: sidePanelRef,
  } = useConversationSidePanelContext();
  const router = useAppRouter();
  const { conversationParticipants } = useConversationParticipants({
    conversationId: conversation.sId,
    workspaceId: owner.sId,
  });
  // Esc belongs to the editors (deselect, cancel a cell edit), so full screen exits by button only.
  const { isFullScreen, enterFullScreen, exitFullScreen, leaveFullScreen } =
    usePanelFullScreen({ exitOnEscape: false });
  // A space is the conversation's desktop: it widens the panel (the panel's own width transition
  // animates it) and gives the chat back its room when it closes. Full screen stays one click away.
  const { connection } = useLiveDocConnection({ owner, docId });
  const isSpace = connection?.liveDoc.kind === "space";
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sidePanel = sidePanelRef.current;
    if (!isSpace || !sidePanel || sidePanel.getSize() >= desktopPanelSize) {
      return;
    }
    sidePanel.resize(desktopPanelSize);
    return () => {
      if (
        !sidePanel.isCollapsed() &&
        sidePanel.getSize() === desktopPanelSize
      ) {
        sidePanel.resize(DEFAULT_RIGHT_PANEL_SIZE);
      }
    };
  }, [isSpace, docId, sidePanelRef]);

  // Leaving plays the exit before the panel switches; a timer, not animationend, so a skipped or
  // interrupted animation can never strand the panel.
  const [isLeaving, setIsLeaving] = useState(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(leaveTimerRef.current ?? undefined), []);
  const leaveThen = (action: () => void) => {
    if (leaveTimerRef.current) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      action();
      return;
    }
    setIsLeaving(true);
    leaveTimerRef.current = setTimeout(action, panelExitMs);
  };

  return (
    <MemoairLiveHostProvider owner={owner}>
      <div
        ref={panelRef}
        className={cn(
          "relative h-full min-h-0 origin-right motion-reduce:animate-none",
          isLeaving
            ? "pointer-events-none animate-out fade-out-0 zoom-out-[0.98] slide-out-to-right-2 fill-mode-forwards duration-200 ease-in"
            : "animate-in fade-in-0 zoom-in-[0.96] slide-in-from-right-6 fill-mode-backwards delay-75 duration-[550ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
        )}
      >
        <LiveObjectView
          className="sheet h-full min-h-0"
          docId={docId}
          agents={conversationParticipants?.agents ?? []}
          callMembers={callMembersOf(conversationParticipants)}
          renderHeader={({ title, actions }) => (
            <ConversationSidePanelHeader
              title={title}
              backTooltip="Back to In this chat"
              onBack={
                backToDetails &&
                (() =>
                  leaveThen(() => {
                    leaveFullScreen();
                    backToDetails();
                  }))
              }
              onClose={() =>
                leaveThen(() => {
                  leaveFullScreen();
                  markLivePanelClosed(conversation.sId);
                  closePanel();
                })
              }
            >
              <div className="flex items-center gap-1">
                {actions}
                <Button
                  size="xs"
                  variant="ghost"
                  icon={isFullScreen ? Minimize01 : Maximize01}
                  tooltip={isFullScreen ? "Exit full screen" : "Full screen"}
                  aria-label={isFullScreen ? "Exit full screen" : "Full screen"}
                  onClick={isFullScreen ? exitFullScreen : enterFullScreen}
                />
                <Button
                  size="xs"
                  variant="ghost"
                  icon={LinkExternal01}
                  tooltip="Open as its own page"
                  aria-label="Open as its own page"
                  onClick={() => {
                    leaveFullScreen();
                    void router.push(getLiveDocPageRoute(owner.sId, docId));
                  }}
                />
              </div>
            </ConversationSidePanelHeader>
          )}
        />
      </div>
    </MemoairLiveHostProvider>
  );
}
